import { type ClassValue, clsx } from "clsx";
import { saveAs } from "file-saver";
import { twMerge } from "tailwind-merge";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/**
 * Detects if running on iOS (iPhone, iPad, iPod)
 */
const isIOS = (): boolean => {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent;
  // iPad on iOS 13+ reports as MacIntel but has touch
  return (
    /iPad|iPhone|iPod/.test(ua) ||
    (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1)
  );
};

/**
 * Triggers a file download from a URL.
 *
 * Steps:
 * 1. Checks if iOS.
 * 2. If iOS: Uses FileSaver.js (fetch + saveAs) to verify filename and ensure download (native approach often fails or previews).
 * 3. If Desktop/Android: Uses standard anchor tag with download attribute (most reliable, preserves user gesture).
 */
export const triggerDownload = async (url: string): Promise<void> => {
  // Default filename fallback (mostly unused as we rely on headers or anchor download)
  let filename = url.split("/").pop()?.split("?")[0] || "document.pdf";

  if (isIOS()) {
    try {
      const response = await fetch(url, { credentials: "include" });
      if (!response.ok) throw new Error("Download failed");

      // Extract filename from Content-Disposition header
      const disposition = response.headers.get("Content-Disposition");
      if (disposition) {
        const match = disposition.match(/filename="([^"]+)"/);
        if (match && match[1]) {
          filename = match[1];
        }
      }

      const blob = await response.blob();
      saveAs(blob, filename);
    } catch (error) {
      console.warn("Download failed, falling back to direct navigation", error);
      window.location.href = url;
    }
    return;
  }

  // Non-iOS: Use standard anchor tag
  // This avoids "popup blocked" issues on desktop caused by async fetch losing user gesture
  const a = document.createElement("a");
  a.href = url;
  a.target = "_blank";
  a.rel = "noopener noreferrer";
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
};
